import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { runBusinessAudit, latestBusinessAudit } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { ClipboardText, Lightning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const scoreColor = (v) => (v >= 75 ? "text-emerald-400" : v >= 50 ? "text-amber-300" : "text-red-400");
const barColor = (v) => (v >= 75 ? "bg-emerald-500" : v >= 50 ? "bg-amber-400" : "bg-red-500");
const AREA_LABELS = {
  website: "Website", social: "Social Media", email: "Email Marketing", brand: "Brand",
  google_business: "Google Business", seo: "SEO", competitors: "Competitors", marketing: "Overall Marketing",
};

export default function BusinessAuditPage() {
  const { activeId, activeClient } = useClient();
  const qc = useQueryClient();
  const { data: report } = useQuery({
    queryKey: ["business-audit", activeId],
    queryFn: () => latestBusinessAudit(activeId), enabled: !!activeId,
  });
  const run = useMutation({
    mutationFn: () => runBusinessAudit(activeId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["business-audit", activeId] }); toast.success("Business audit complete"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Audit failed"),
  });
  const r = report?.id ? report : null;

  return (
    <PageContainer>
      <PageHeader icon={ClipboardText} eyebrow="AI Command" title="AI Business Audit"
        subtitle={`One click — your fractional CMO audits ${activeClient?.name || "your business"} across website, social, email, brand, Google presence, SEO, competitors and overall marketing, then hands you a 30-day improvement plan.`} />

      <div className="card-matte p-5 mb-5 flex flex-wrap items-center gap-4" data-testid="audit-run-card">
        <div className="flex-1 min-w-[240px]">
          <div className="font-head font-semibold">Run a complete audit</div>
          <div className="text-xs text-[#9a9a9a] mt-0.5">Uses live signals from your workspace — connections, publishing activity, brand kit, SEO items and your latest website analysis. Takes ~30 seconds.</div>
        </div>
        <Button data-testid="audit-run-btn" onClick={() => run.mutate()} disabled={run.isPending || !activeId}
          className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-11 px-6">
          <Lightning size={16} className="mr-1.5" weight="fill" /> {run.isPending ? "Auditing…" : r ? "Re-run Audit" : "Run AI Audit"}
        </Button>
      </div>

      {r ? (
        <div data-testid="audit-report">
          <div className="card-matte p-6 mb-5 flex flex-wrap items-center gap-6">
            <div className="text-center">
              <div className={`font-head font-extrabold text-6xl ${scoreColor(r.overall)}`} data-testid="audit-overall-score">{r.overall}</div>
              <div className="text-[10px] text-[#7a7a7a] mt-1">marketing health / 100</div>
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] text-[#7a7a7a] mb-1">Audited {(r.created_at || "").slice(0, 16).replace("T", " ")}</div>
              <p className="font-head font-semibold text-lg leading-snug" data-testid="audit-headline">{r.headline}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="audit-areas">
            {(r.areas || []).map((a, i) => (
              <div key={i} className="card-matte p-5" data-testid={`audit-area-${a.area}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-head font-semibold text-sm">{AREA_LABELS[a.area] || a.area}</span>
                  <span className={`font-head font-extrabold text-xl ${scoreColor(a.score)}`}>{a.score}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-3">
                  <div className={`h-full ${barColor(a.score)}`} style={{ width: `${a.score}%` }} />
                </div>
                <p className="text-xs text-[#9a9a9a] mb-3">{a.finding}</p>
                <div className="space-y-1.5">
                  {(a.actions || []).map((act, j) => (
                    <div key={j} className="flex items-start gap-2 text-xs text-[#cfcfcf]">
                      <CheckCircle size={13} className="brand-text shrink-0 mt-0.5" /> {act}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {(r.plan_30_days || []).length > 0 && (
            <div className="card-matte brand-glow p-6" data-testid="audit-plan">
              <div className="label-caps mb-4 brand-text">Your 30-day improvement plan</div>
              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                {r.plan_30_days.map((w) => (
                  <div key={w.week} className="rounded-xl border border-white/8 bg-white/[0.02] p-4" data-testid={`audit-week-${w.week}`}>
                    <div className="font-head font-bold text-sm brand-text mb-1">Week {w.week}</div>
                    <div className="text-xs font-semibold text-white mb-2">{w.focus}</div>
                    <ul className="space-y-1.5">
                      {(w.tasks || []).map((t, j) => (
                        <li key={j} className="text-[11px] text-[#9a9a9a] flex gap-1.5"><span className="brand-text">→</span> {t}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card-matte p-10 text-center text-sm text-[#7a7a7a]" data-testid="audit-empty">
          {run.isPending ? "Your AI CMO is auditing everything…" : "No audit yet — run your first one above."}
        </div>
      )}
    </PageContainer>
  );
}
